/**
 * Ticking off a task that is not yet up for doing (spec §9.5 "not before") asks once: the next
 * occurrence of a weekly task, a task set aside, a recurring task inside its minimum gap. Doing it early
 * stays possible — it is the person's call — but never happens by a stray click.
 */
import type { TFunction } from "i18next";
import type { Task } from "../core/schema";
import type { LoroDoc } from "loro-crdt";
import { notBeforeMs } from "../core/notYet";
import { completeTask, uncompleteTask } from "../core/complete";

/** "Mon 6 Oct", plus the time when it is not midnight. */
export function formatNotBefore(ms: number, lang: string): string {
  const d = new Date(ms);
  const day = new Intl.DateTimeFormat(lang, { weekday: "short", day: "numeric", month: "short" }).format(d);
  return d.getHours() === 0 && d.getMinutes() === 0
    ? day
    : `${day}, ${new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(d)}`;
}

/** True when the task may be marked done now: it is due for doing, or the person confirmed. */
export function confirmEarlyDone(t: TFunction, task: Task, nowMs: number, lang: string): boolean {
  if (task.doneAt) return true;
  const nb = notBeforeMs(task, nowMs);
  if (nb === null) return true;
  return window.confirm(t("task.notYetConfirm", { title: task.title, when: formatNotBefore(nb, lang) }));
}

/**
 * Mark a task done or not done: the one done control behind the panel, List, *Wants doing*, Kanban and Focus
 * (UI cleanup U2: each repeated the early-done question and the write). Returns false when the person declined.
 */
export function setDone(
  write: (fn: (doc: LoroDoc) => void) => void,
  t: TFunction,
  lang: string,
  task: Task,
  done: boolean,
): boolean {
  if (done && !task.doneAt && !confirmEarlyDone(t, task, Date.now(), lang)) return false;
  write((doc) => (done ? completeTask(doc, task.id) : uncompleteTask(doc, task.id)));
  return true;
}
